import { StatusChip, type StatusTone } from "@prisma/ui";
import { useQuery } from "@tanstack/react-query";
import { CalendarClock, Camera, Clapperboard, Image, ScissorsLineDashed, ShieldCheck, UserRound, Video, type LucideIcon } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Link, useSearchParams } from "react-router";

import { PageHeader } from "../../components/PageHeader";
import { QueryState } from "../../components/QueryState";
import { api, call } from "../../lib/api";
import type { components } from "../../lib/api-types";
import { formatDate } from "../../lib/format";
import { useSession } from "../../lib/session";

type Board = components["schemas"]["ProductionBoardOut"];
type Job = components["schemas"]["JobCardOut"];
type Product = components["schemas"]["ProductOut"];
type JobType = "AI_IMAGE" | "AI_VIDEO" | "SHOOTING" | "MEDIA_PREP" | "EDITING";

const COLUMNS = ["todo", "in_progress", "review", "revision", "approved"] as const;
const TYPES: JobType[] = ["SHOOTING", "EDITING", "MEDIA_PREP", "AI_IMAGE", "AI_VIDEO"];
const PRIORITIES = ["NORMAL", "URGENT", "CRITICAL"] as const;
const COLUMN_TONE: Record<string, StatusTone> = { todo: "planning", in_progress: "production", review: "review", revision: "warning", approved: "success" };
export const JOB_ICON: Record<string, LucideIcon> = { SHOOTING: Camera, EDITING: ScissorsLineDashed, MEDIA_PREP: Clapperboard, AI_IMAGE: Image, AI_VIDEO: Video };

/** Production Board (doc 04 P10). Live: job events refresh the board. Optional preset filter
 *  (the Shooting page is the same board filtered to Shooting Jobs). */
export function ProductionBoardPage({ preset }: { preset?: { job_type: JobType; titleKey: string; subtitleKey: string } }) {
  const { t } = useTranslation();
  const me = useSession();
  const [params, setParams] = useSearchParams();
  const filters = {
    product: params.get("product") || undefined,
    job_type: (preset?.job_type ?? (params.get("job_type") || undefined)) as JobType | undefined,
    assignee: params.get("assignee") || undefined,
    priority: (params.get("priority") || undefined) as (typeof PRIORITIES)[number] | undefined,
  };
  const q = useQuery({ queryKey: ["jobs", filters],
    queryFn: () => call<Board>(api.GET("/app/v1/production/board", { params: { query: filters } })) });
  const products = useQuery({ queryKey: ["products"], queryFn: () => call<Product[]>(api.GET("/app/v1/products")),
    enabled: Boolean(me.permissions["product.view"]) });
  const set = (k: string, v: string | null) => {
    const next = new URLSearchParams(params);
    if (v) next.set(k, v);
    else next.delete(k);
    setParams(next, { replace: true });
  };
  const select = "h-10 rounded-[var(--radius-control)] border border-line bg-surface px-3 text-[14px] text-ink";
  return (
    <div className="space-y-6">
      <PageHeader titleKey={preset?.titleKey ?? "production.title"} subtitle={t(preset?.subtitleKey ?? "production.subtitle")} />
      <div className="flex flex-wrap items-center gap-3">
        <label className="sr-only" htmlFor="pb-product">{t("requests.col_product")}</label>
        <select id="pb-product" className={select} value={filters.product ?? ""} onChange={(e) => set("product", e.target.value || null)}>
          <option value="">{t("requests.all_products")}</option>
          {(products.data ?? []).map((p) => <option key={p.id} value={p.code}>{p.name}</option>)}
        </select>
        {!preset && (
          <>
            <label className="sr-only" htmlFor="pb-type">{t("production.job_type")}</label>
            <select id="pb-type" className={select} value={filters.job_type ?? ""} onChange={(e) => set("job_type", e.target.value || null)}>
              <option value="">{t("production.all_types")}</option>
              {TYPES.map((x) => <option key={x} value={x}>{t(`plan.job_type_${x.toLowerCase()}`)}</option>)}
            </select>
          </>
        )}
        <label className="sr-only" htmlFor="pb-assignee">{t("production.assignee")}</label>
        <select id="pb-assignee" className={select} value={filters.assignee ?? ""} onChange={(e) => set("assignee", e.target.value || null)}>
          <option value="">{t("production.anyone")}</option>
          <option value={me.id}>{t("production.assigned_to_me")}</option>
          <option value="unassigned">{t("requests.unassigned")}</option>
        </select>
        <label className="sr-only" htmlFor="pb-priority">{t("requests.col_priority")}</label>
        <select id="pb-priority" className={select} value={filters.priority ?? ""} onChange={(e) => set("priority", e.target.value || null)}>
          <option value="">{t("requests.all_priorities")}</option>
          {PRIORITIES.map((p) => <option key={p} value={p}>{t(`requests.priority_${p.toLowerCase()}`)}</option>)}
        </select>
      </div>
      <QueryState query={q}>
        {(b) => (
          <div className="grid grid-cols-1 gap-4 md:grid-cols-3 xl:grid-cols-5">
            {COLUMNS.map((c) => (
              <section key={c} aria-labelledby={`col-${c}`} className="flex min-h-64 flex-col rounded-[var(--radius-card)] border border-line bg-elevated p-3">
                <h2 id={`col-${c}`} className="mb-3 flex items-center justify-between px-1">
                  <StatusChip tone={COLUMN_TONE[c] ?? "neutral"} icon={null} label={t(`production.col_${c}`)} />
                  <span className="text-[13px] text-ink-2">{(b.columns[c] ?? []).length}</span>
                </h2>
                <ul className="space-y-3">
                  {(b.columns[c] ?? []).map((j) => <JobCard key={j.id} j={j} tz={me.time_zone} />)}
                </ul>
                {(b.columns[c] ?? []).length === 0 && <p className="px-1 text-[13px] text-ink-2">{t("production.col_empty")}</p>}
              </section>
            ))}
          </div>
        )}
      </QueryState>
    </div>
  );
}

function JobCard({ j, tz }: { j: Job; tz: string }) {
  const { t, i18n } = useTranslation();
  const Icon = JOB_ICON[j.job_type] ?? Clapperboard;
  return (
    <li>
      <Link to={`/production/jobs/${j.display_id}`} className="block overflow-hidden rounded-[var(--radius-card)] border border-line bg-surface shadow-[var(--shadow-card)] hover:border-[var(--border-strong)]">
        <div className="relative flex aspect-[16/9] items-center justify-center bg-soft">
          <div aria-hidden="true" className="absolute inset-0 bg-[image:var(--gradient-signature)] opacity-[0.08]" />
          <Icon aria-hidden="true" className="relative size-7 text-ink-2" strokeWidth={1.5} />
          <span className="absolute left-2 top-2 rounded-md bg-surface px-1.5 py-0.5 text-[11px] font-medium text-ink">{t(`plan.job_type_${j.job_type.toLowerCase()}`)}</span>
        </div>
        <div className="space-y-1.5 p-3 text-[13px]">
          <div className="flex items-center justify-between gap-2">
            <span className="whitespace-nowrap font-semibold text-ink">{j.display_id}</span>
            {j.phase && <span className="truncate text-right text-[11px] text-ink-2">{t(`production.phase_${j.phase.toLowerCase()}`)}</span>}
          </div>
          <div className="truncate text-ink">{j.product.name}{j.campaign_id ? ` · ${j.campaign_id}` : ""}</div>
          {j.subject && <div className="truncate text-ink-2">{j.subject}</div>}
          <div className="flex items-center gap-1.5 text-ink-2">
            <UserRound aria-hidden="true" className="size-3.5" />{j.assignee?.name ?? t("requests.unassigned")}
          </div>
          {j.deadline && (
            <div className="flex items-center gap-1.5 text-ink-2">
              <CalendarClock aria-hidden="true" className="size-3.5" />{formatDate(j.deadline, i18n.language, tz)}
            </div>
          )}
          {j.consent_status === "PENDING" && (
            <div className="flex items-center gap-1.5 text-[var(--status-warning)]">
              <ShieldCheck aria-hidden="true" className="size-3.5" />{t("production.consent_pending")}
            </div>
          )}
        </div>
      </Link>
    </li>
  );
}
